// lib/leasing/leadStatus.ts
//
// ONE answer to "is this row still on the shortlist", for Lead / PortfolioFit / UnitFit alike.
//
// This existed as eleven inline literals that had already drifted apart, and the drift was
// load-bearing rather than cosmetic: the campaign LIST route excluded two statuses while the
// campaign DETAIL route excluded three, so the card and the page reported different totals for
// the same campaign. Sibling grids disagreed in opposite directions, one hid only DISCARDED,
// the other only DISMISSED. Every one of those looked like an isolated bug and got an isolated
// fix, in one copy out of eleven.
//
// THE DECISION THE COPIES WERE MISSING. `DISMISSED` used to mean "a human took this off the
// list". It does not any more: since the Judge began writing DISMISSED for every "not a fit"
// verdict, it is a SCORE, the ordinary outcome of analysing a brand that does not suit the
// asset, and the reason a 200-brand run can produce 188 of them. Human curation is INVALIDATED
// (set by hand) and DISCARDED (removed from the run). So:
//
//   - a DISMISSED row is a RESULT. It was analysed, it was billed, it belongs in the count, and
//     re-weighting the rubric may legitimately revive it.
//   - an INVALIDATED or DISCARDED row is a DECISION a person made. It stays out, and no
//     arithmetic on the weights may undo it.
//
// Everything here follows from that one sentence. If the product meaning changes, change it here.

/** Statuses that mean a PERSON took the row off the list. Never produced by scoring. */
export const HUMAN_REMOVED_STATUSES = ['DISCARDED', 'INVALIDATED'] as const;

/** Every status that keeps a row out of the visible shortlist. Read through `isSetAside`. */
const SET_ASIDE_STATUSES = ['DISMISSED', ...HUMAN_REMOVED_STATUSES] as const;

/** Statuses that mean the row was actually scored, so it must not be re-analysed for free. */
export const SCORED_STATUSES = ['SUCCESS', 'INTERESTING', 'DISMISSED', 'INVALIDATED'] as const;

/**
 * The only statuses allowed to LEAVE THE BUILDING, in an exported document sent to a landlord.
 *
 * An ALLOWLIST, not the complement of `isSetAside`, and that is the whole point: the ways a row can
 * be unfit to send keep multiplying (FAILED carries the raw exception text as its rationale,
 * REVERTED and PENDING keep a score from a run that has been superseded, ABORTED never finished),
 * and every one of them was invisible to a list of things to exclude. A document cannot be
 * recalled, so a status nobody has thought about must default to staying in.
 */
export const SHAREABLE_STATUSES = ['SUCCESS', 'INTERESTING'] as const;

const normalize = (status: unknown): string =>
  typeof status === 'string' ? status.trim().toUpperCase() : '';

/** Sunk to the bottom of the shortlist, whatever put it there. */
export const isSetAside = (status: unknown): boolean =>
  (SET_ASIDE_STATUSES as readonly string[]).includes(normalize(status));

/**
 * Removed by a person, so it must survive a re-score. The narrower half of `isSetAside`, and the
 * one the rubric re-apply pins, pinning DISMISSED instead meant re-weighting the very criterion
 * that rejected a brand visibly did nothing to the rows it rejected.
 */
export const isHumanRemoved = (status: unknown): boolean =>
  (HUMAN_REMOVED_STATUSES as readonly string[]).includes(normalize(status));

/**
 * Counted on a campaign card or page. DISMISSED is INCLUDED: the run analysed it and the customer
 * paid for it, and excluding it made a 200-brand campaign report "12 leads".
 */
export const isVisibleFit = (status: unknown): boolean => !isHumanRemoved(status);

/** Already scored by some run, so a later pass must not silently redo it. */
export const isScored = (status: unknown): boolean =>
  (SCORED_STATUSES as readonly string[]).includes(normalize(status));
